import React, { useState, useEffect, useCallback } from 'react';
import {
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  getTemplateDependencies,
  archiveTaskTemplate,
  ARCHIVE_REASON_LABELS,
  type TaskTemplateListItem,
  type TemplateDependencyCheckResult,
  type TemplateArchiveReason,
} from './templateManagementService';

interface ArchiveTemplateModalProps {
  template: TaskTemplateListItem;
  onClose: () => void;
  onArchived: () => void;
}

const ArchiveTemplateModal: React.FC<ArchiveTemplateModalProps> = ({ template, onClose, onArchived }) => {
  const [step, setStep] = useState<'loading' | 'form' | 'submitting'>('loading');
  const [dependencies, setDependencies] = useState<TemplateDependencyCheckResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state
  const [archiveReason, setArchiveReason] = useState<TemplateArchiveReason | ''>('');
  const [archiveNotes, setArchiveNotes] = useState('');
  const isJurisdictionBase = template.is_system_template && template.template_kind === 'jurisdiction_base';
  const [cascadeToVariants, setCascadeToVariants] = useState(isJurisdictionBase);

  const loadDependencies = useCallback(async () => {
    try {
      setStep('loading');
      setLoadError(null);
      const result = await getTemplateDependencies(template.id);
      setDependencies(result);
      setStep('form');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load dependencies');
      setStep('form');
    }
  }, [template.id]);

  useEffect(() => {
    loadDependencies();
  }, [loadDependencies]);

  const handleSubmit = async () => {
    if (!archiveReason) return;
    try {
      setStep('submitting');
      setSubmitError(null);
      await archiveTaskTemplate(template.id, {
        archive_reason: archiveReason,
        archive_notes: archiveNotes || undefined,
        cascade_to_variants: cascadeToVariants,
      });
      onArchived();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to archive template');
      setStep('form');
    }
  };

  const activeChildVariants = dependencies?.child_variants.filter((c) => !c.is_archived) || [];
  const hasActiveTasks = (dependencies?.active_matter_count ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
        style={{ animation: 'lex-overlay-in 150ms ease-out forwards' }}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ animation: 'lex-modal-in 220ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <ArchiveBoxIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Archive Template</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">{template.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Loading state */}
          {step === 'loading' && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-lex-accent border-t-transparent" />
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Checking dependencies...</span>
            </div>
          )}

          {/* Load error */}
          {loadError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
              {loadError}
            </div>
          )}

          {step !== 'loading' && (
            <>
              {/* Active task warning */}
              {hasActiveTasks && dependencies && (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Active Tasks Found
                      </p>
                      <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                        This template is currently used by {dependencies.active_matter_count} matter(s).
                        Archiving will NOT affect existing tasks — they will continue to function.
                        However, no new tasks can be created from this template.
                      </p>
                      {/* Matter list */}
                      <div className="mt-3 space-y-1">
                        {dependencies.matters.slice(0, 10).map((m) => (
                          <div key={m.matter_id} className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-300">
                            <span className="font-mono">{m.matter_number}</span>
                            <span>{m.active_task_count} active task(s)</span>
                          </div>
                        ))}
                        {dependencies.matters.length > 10 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            ...and {dependencies.matters.length - 10} more
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Child variants */}
              {activeChildVariants.length > 0 && (
                <div className="p-4 rounded-lg bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-lex-accent dark:text-lex-accent mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-lex-accent dark:text-lex-accent">
                        {activeChildVariants.length} Child Variant(s)
                      </p>
                      <div className="mt-2 space-y-1">
                        {activeChildVariants.map((v) => (
                          <div key={v.id} className="flex items-center justify-between text-xs text-lex-accent dark:text-lex-accent">
                            <span>{v.name}</span>
                            <span className="text-lex-accent dark:text-lex-accent">
                              {v.template_kind.replace(/_/g, ' ')} · {v.usage_count} uses
                            </span>
                          </div>
                        ))}
                      </div>
                      <label className="mt-3 flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cascadeToVariants}
                          onChange={(e) => setCascadeToVariants(e.target.checked)}
                          className="rounded border-lex-accent text-lex-accent focus:ring-lex-accent"
                        />
                        <span className="text-sm text-lex-accent dark:text-lex-accent">
                          Also archive {activeChildVariants.length} child variant(s)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Archive Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Archive Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value as TemplateArchiveReason)}
                  className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent transition-colors text-sm"
                >
                  <option value="">Select a reason...</option>
                  {Object.entries(ARCHIVE_REASON_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Archive Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Archive Notes <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={archiveNotes}
                  onChange={(e) => setArchiveNotes(e.target.value)}
                  placeholder="Additional notes about archiving this template..."
                  rows={3}
                  className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent transition-colors text-sm resize-none"
                />
              </div>

              {/* Impact summary */}
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Impact Summary</p>
                <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <li>• Template will move to the Archived Templates section</li>
                  <li>• No new tasks can be created from this template</li>
                  <li>• Existing tasks created from this template are unaffected</li>
                  <li>• Can be restored from Archived Templates at any time</li>
                </ul>
              </div>

              {/* Submit error */}
              {submitError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {step !== 'loading' && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!archiveReason || step === 'submitting'}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              {step === 'submitting' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Archiving...
                </>
              ) : (
                <>
                  <ArchiveBoxIcon className="h-4 w-4" />
                  Archive Template
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchiveTemplateModal;
